<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Table, TableHeader, TableRow, TableHead, TableCell, TableBody } from '../table';
import { Button } from '../button';
import { Input } from '../input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../sheet';
import { facilitiesList, groupsByGroupID, groupsFetchAll } from '../facilities';
import type { Facility, FacilityID, TenantID } from '../../cross-app-shared/types/db';
import FacilityFullDetailsSheet from './FacilityFullDetailsSheet.vue';
import FacilityCreateSheet from './FacilityCreateSheet.vue';
import { crossAppActions } from '../cross-app-actions';
import { alertModal } from '../modals/AlertModal.vue';
import { toast } from '../toast';

const props = defineProps<{ facility: Facility | null }>();

// Import image for visual header
import facilitiesImg from '../../tri-frontend/assets/woman_square.png';

const searchQuery = ref('');
const filteredFacilities = computed(() => {
  if (!searchQuery.value) return facilitiesList.value;
  const query = searchQuery.value.toLowerCase();
  return facilitiesList.value.filter(
    (f) => f.name.toLowerCase().includes(query) || f.groupName.toLowerCase().includes(query),
  );
});

const facilityCreateSheetOpen = ref(false);
const facilityEditSheetOpen = ref(false);
const facilityEditing = ref<FacilityID | null>(null);

const tenantsAll = computed(() => {
  const result: { tenantID: TenantID; tenantName: string }[] = [];
  for (const group of Object.values(groupsByGroupID.value)) {
    const existing = result.find((t) => t.tenantID === group.tenantID);
    if (!existing) {
      result.push({ tenantID: group.tenantID, tenantName: group.tenantName });
    }
  }

  result.sort((a, b) => a.tenantName.localeCompare(b.tenantName));

  return result;
});
const groupNamesVisible = computed(() => {
  return Array.from(new Set<string>(filteredFacilities.value.map((f) => f.groupName))).sort();
});
const newGroupTenant = ref(props.facility?.tenantID ?? (-1 as TenantID));
const newGroupName = ref('');
const addGroupOpen = ref(false);

const handleAddGroup = async () => {
  if (
    newGroupName.value &&
    !Object.values(groupsByGroupID.value).find(
      (g) => g.tenantID === newGroupTenant.value && g.name === newGroupName.value,
    )
  ) {
    try {
      await crossAppActions.facilities.groupCreate({
        tenantID: newGroupTenant.value,
        groupName: newGroupName.value,
      });
      await groupsFetchAll();
      toast({
        title: 'Group created',
        description: `The ${newGroupName.value} group is now available`,
      });
    } catch (e: any) {
      console.error(e);
      await alertModal({
        title: 'Unable to create group',
        message: `An unexpected error occurred and we were unable to create your group. Error was: ${e.mesasge}`,
      });
    }
  }

  newGroupName.value = '';
  addGroupOpen.value = false;
};

const activeView = ref('all');

const filteredFacilitiesByTab = computed(() => {
  if (activeView.value === 'all') {
    return filteredFacilities.value;
  }
  return filteredFacilities.value.filter((f) => (f.groupName || 'Ungrouped') === activeView.value);
});

onMounted(() => {
  groupsFetchAll();
});
</script>

<template>
  <div class="w-full">
    <Card>
      <CardHeader>
        <div class="flex items-center gap-4">
          <!-- Facilities Image on the left -->
          <div class="shrink-0">
            <div class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gray-200 bg-white p-2">
              <img :src="facilitiesImg" alt="Facilities" class="h-10 w-10 object-contain" />
            </div>
          </div>
          <!-- Header text and actions -->
          <div class="flex w-full items-start justify-between">
            <div>
              <CardTitle class="text-2xl font-bold">Facility Management</CardTitle>
              <CardDescription class="mt-1">Manage facilities and organizational groups</CardDescription>
            </div>
            <div class="space-x-2">
              <Button @click="facilityCreateSheetOpen = true">
                <i class="fa-light fa-plus"></i>
                Create Facility
              </Button>
              <Button variant="outline" @click="addGroupOpen = true">
                <i class="fa-light fa-layer-group"></i>
                Add Group
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div class="relative mb-4">
          <i class="fa-light fa-magnifying-glass text-muted-foreground absolute top-3 left-2.5 h-4 w-4"></i>
          <Input v-model="searchQuery" type="search" placeholder="Search facilities..." class="w-full pl-8" />
        </div>

        <div class="mb-4">
          <Select v-model="activeView">
            <SelectTrigger class="w-[200px]">
              <SelectValue :placeholder="`${activeView === 'all' ? 'All Facilities' : activeView}`" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Facilities</SelectItem>
              <SelectItem v-for="group of groupNamesVisible" :key="group" :value="group">
                {{ group }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-if="filteredFacilitiesByTab.length === 0">
              <TableCell colspan="4" class="text-muted-foreground py-8 text-center"> No facilities found </TableCell>
            </TableRow>
            <TableRow v-for="facility in filteredFacilitiesByTab" :key="facility.facilityID" class="odd:bg-muted/50">
              <TableCell>{{ facility.facilityID }}</TableCell>
              <TableCell>{{ facility.name }}</TableCell>
              <TableCell>{{ facility.groupName || 'Ungrouped' }}</TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  @click="
                    facilityEditing = facility.facilityID;
                    facilityEditSheetOpen = true;
                  "
                >
                  <i class="fa-light fa-pen-to-square"></i>
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <FacilityCreateSheet
      v-if="facility || facilitiesList.length > 0"
      v-model:isOpen="facilityCreateSheetOpen"
      @saved="searchQuery = $event.name"
      :tenantIDDefault="facility?.tenantID ?? facilitiesList[0].tenantID"
      :facilityGroupIDDefault="facility?.facilityGroupID ?? facilitiesList[0].facilityGroupID"
    />

    <FacilityFullDetailsSheet
      v-if="facilityEditing"
      v-model:isOpen="facilityEditSheetOpen"
      :facilityID="facilityEditing"
    >
      <template v-if="$slots['extra-facility-sections']" #extra-sections="slotProps">
        <slot name="extra-facility-sections" v-bind="slotProps" />
      </template>
    </FacilityFullDetailsSheet>

    <Sheet v-model:open="addGroupOpen">
      <SheetContent side="right" class="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add Group</SheetTitle>
        </SheetHeader>
        <div class="mt-6 space-y-4">
          <select
            v-model="newGroupTenant"
            class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option v-for="tenant of tenantsAll" :key="tenant.tenantID" :value="tenant.tenantID">
              {{ tenant.tenantName }}
            </option>
          </select>
          <Input v-model="newGroupName" placeholder="Group Name" />
          <div class="flex justify-end space-x-4">
            <Button variant="outline" @click="addGroupOpen = false">
              <i class="fa-light fa-xmark"></i>
              Cancel
            </Button>
            <Button @click="handleAddGroup">
              <i class="fa-light fa-plus"></i>
              Add Group
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  </div>
</template>
